// Parse a business-hours string like "Mon-Sat 9:00 AM - 6:00 PM" and tell
// whether the business is open at the current time IN THE BRANCH'S TIME ZONE.
// Returns true (open), false (closed) or null (could not parse -> unknown).

const WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const JS_TO_WEEK = { 0: 6, 1: 0, 2: 1, 3: 2, 4: 3, 5: 4, 6: 5 }; // Sun..Sat -> Mon-index

function toMinutes(h, m, ap) {
  let hh = h % 12;
  if (ap.toUpperCase() === "PM") hh += 12;
  return hh * 60 + m;
}

function cap(s) {
  return s.charAt(0).toUpperCase() + s.slice(1, 3).toLowerCase();
}

// Current weekday index (Mon=0) + minutes-since-midnight, in the given IANA tz (or local).
function nowInZone(tz, now) {
  if (tz) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: tz, weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
      }).formatToParts(now);
      const wd = parts.find((p) => p.type === "weekday").value;
      const hh = +parts.find((p) => p.type === "hour").value;
      const mm = +parts.find((p) => p.type === "minute").value;
      const day = WEEK.indexOf(wd);
      if (day >= 0) return { day, minutes: hh * 60 + mm };
    } catch (e) { /* fall through to local */ }
  }
  return { day: JS_TO_WEEK[now.getDay()], minutes: now.getHours() * 60 + now.getMinutes() };
}

export function isOpenNow(hours, tz, now = new Date()) {
  if (!hours || typeof hours !== "string") return null;
  const re = /([A-Za-z]{3})\s*-\s*([A-Za-z]{3})\s+(\d{1,2}):(\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}):(\d{2})\s*(AM|PM)/i;
  const m = hours.match(re);
  if (!m) return null;

  const sd = WEEK.indexOf(cap(m[1]));
  const ed = WEEK.indexOf(cap(m[2]));
  if (sd < 0 || ed < 0) return null;

  const { day: today, minutes: cur } = nowInZone(tz, now);
  const inDayRange = ed >= sd ? today >= sd && today <= ed : today >= sd || today <= ed;
  if (!inDayRange) return false;

  const open = toMinutes(+m[3], +m[4], m[5]);
  let close = toMinutes(+m[6], +m[7], m[8]);
  if (close === 0) close = 24 * 60; // midnight close
  if (close > open) return cur >= open && cur < close;
  return cur >= open || cur < close; // overnight
}

// ---- Structured opening hours (per-day) at a GMT offset ----
export const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const DAY_LABELS = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
const DAY_SHORT = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
const JS_DAY = { 0: "sun", 1: "mon", 2: "tue", 3: "wed", 4: "thu", 5: "fri", 6: "sat" };

// GMT offsets from -12 to +14 (common half-hour zones included).
export const GMT_OFFSETS = [-12, -11, -10, -9.5, -9, -8, -7, -6, -5, -4, -3.5, -3, -2, -1, 0, 1, 2, 3, 3.5, 4, 4.5, 5, 5.5, 5.75, 6, 6.5, 7, 8, 8.75, 9, 9.5, 10, 10.5, 11, 12, 12.75, 13, 14];

export function formatGmt(off) {
  off = off || 0;
  const sign = off < 0 ? "-" : "+";
  const a = Math.abs(off);
  const h = Math.floor(a);
  const m = Math.round((a - h) * 60);
  return `GMT${sign}${h}${m ? ":" + String(m).padStart(2, "0") : ""}`;
}

function to12h(t) {
  const [h, m] = (t || "09:00").split(":").map(Number);
  const ap = h < 12 ? "AM" : "PM";
  const hh = h % 12 || 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
}

// Current {day-key, minutes} at a fixed GMT offset (in hours).
function nowAtOffset(off, now) {
  const local = new Date(now.getTime() + now.getTimezoneOffset() * 60000 + (off || 0) * 3600000);
  return { key: JS_DAY[local.getDay()], minutes: local.getHours() * 60 + local.getMinutes() };
}

export function isOpenNowStructured(oh, now = new Date()) {
  if (!oh || !oh.days) return null;
  const { key, minutes: cur } = nowAtOffset(oh.tzOffset, now);
  const day = oh.days[key];
  if (!day) return null;
  if (day.closed) return false;
  const [oH, oM] = (day.open || "09:00").split(":").map(Number);
  const [cH, cM] = (day.close || "18:00").split(":").map(Number);
  const open = oH * 60 + oM;
  let close = cH * 60 + cM;
  if (close === 0) close = 1440;
  if (close > open) return cur >= open && cur < close;
  return cur >= open || cur < close;
}

export function isOnVacation(v, now = new Date()) {
  if (!v || !v.active) return false;
  if (v.until) {
    const u = new Date(v.until);
    if (!isNaN(u) && u < now) return false;
  }
  return true;
}

// Overall open state for a deal, honouring vacation, then business hours (source of truth),
// falling back to any legacy per-branch/deal hours string only when structured hours are absent.
export function dealOpenState(deal, branch, now = new Date()) {
  if (isOnVacation(deal?.vacation, now)) return "vacation";
  const r = isOpenNowStructured(deal?.openingHours, now);
  if (r !== null) return r ? "open" : "closed";
  const legacy = (branch && branch.hours) || deal?.hours;
  const r2 = isOpenNow(legacy, branch?.timezone, now);
  return r2 === null ? "unknown" : r2 ? "open" : "closed";
}

// Compact summary grouping consecutive identical days, e.g. "Mon–Fri 9:00 AM - 6:00 PM · Sat closed".
export function formatOpeningHours(oh) {
  if (!oh || !oh.days) return "";
  const segs = [];
  let i = 0;
  while (i < DAY_KEYS.length) {
    const d = DAY_KEYS[i];
    const cur = oh.days[d] || {};
    const keyOf = (x) => (x.closed ? "closed" : `${x.open}-${x.close}`);
    let j = i;
    while (j + 1 < DAY_KEYS.length && keyOf(oh.days[DAY_KEYS[j + 1]] || {}) === keyOf(cur)) j++;
    const label = i === j ? DAY_SHORT[DAY_KEYS[i]] : `${DAY_SHORT[DAY_KEYS[i]]}–${DAY_SHORT[DAY_KEYS[j]]}`;
    segs.push(cur.closed ? `${label} closed` : `${label} ${to12h(cur.open)} - ${to12h(cur.close)}`);
    i = j + 1;
  }
  return segs.join(" · ");
}

export function defaultOpeningHours() {
  return { tzOffset: 0, days: Object.fromEntries(DAY_KEYS.map((d) => [d, { closed: d === "sun", open: "09:00", close: "18:00" }])) };
}
